// src/components/CustomerInvoices.js - EXACT ADMIN PORTAL PDF
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  Paper,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Download,
  Payment,
  Receipt,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { collection, query, onSnapshot, where } from "firebase/firestore";
import { db } from "../firebase";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function CustomerInvoices() {
  const { customer } = useCustomerAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoImage, setLogoImage] = useState(null);

  // Load logo image
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const logoUrl = "https://firebasestorage.googleapis.com/v0/b/humbertack-workflow.firebasestorage.app/o/Minimalist%20HumberTrack%20Logo%20Design.png?alt=media&token=7fac08dd-27a3-466a-8172-48d50a827959";
        
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          setLogoImage(canvas.toDataURL("image/png"));
        };
        img.onerror = () => {
          console.log("Logo failed to load, continuing without it");
        };
        img.src = logoUrl;
      } catch (error) {
        console.error("Error loading logo:", error);
      }
    };

    loadLogo();
  }, []);

  // Calculate total amount from items array
  const calculateTotal = (invoice) => {
    // If invoice has total field, use it
    if (invoice.total && invoice.total > 0) {
      return invoice.total;
    }
    if (invoice.totalAmount && invoice.totalAmount > 0) {
      return invoice.totalAmount;
    }

    // Otherwise calculate from items
    if (invoice.items && Array.isArray(invoice.items)) {
      return invoice.items.reduce((sum, item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const rate = parseFloat(item.rate || item.price) || 0;
        return sum + (quantity * rate);
      }, 0);
    }

    return 0;
  };

  // Calculate subtotal and VAT
  const calculateInvoiceAmounts = (invoice) => {
    const total = calculateTotal(invoice);
    const vatRate = invoice.vatRate || 0;
    
    if (vatRate > 0) {
      const subtotal = total / (1 + (vatRate / 100));
      const vat = total - subtotal;
      return { subtotal, vat, total };
    }
    
    return { subtotal: total, vat: 0, total };
  };

  // Fetch ALL invoices and filter with super flexible matching
  useEffect(() => {
    if (!customer) return;

    setLoading(true);
    setError(null);

    const invoicesQuery = query(
      collection(db, "invoices"),
      where("clientId", "==", customer.id)
    );

    const unsubscribe = onSnapshot(
      invoicesQuery,
      (snapshot) => {
        const invoicesList = snapshot.docs
          .map(doc => {
            const data = doc.data();
            const amounts = calculateInvoiceAmounts(data);
            
            return {
              id: doc.id,
              ...data,
              subtotal: amounts.subtotal,
              vat: amounts.vat,
              total: amounts.total,
            };
          });
        
        invoicesList.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        
        setInvoices(invoicesList);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching invoices:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [customer]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "unpaid":
        return "error";
      case "overdue":
        return "error";
      case "partially paid":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return <CheckCircle />;
      case "unpaid":
      case "overdue":
        return <ErrorIcon />;
      case "partially paid":
        return <Warning />;
      default:
        return <Receipt />;
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status?.toLowerCase() === "paid") return false;
    if (!dueDate) return false;
    const due = dueDate.toDate ? dueDate.toDate() : new Date(dueDate);
    return due < new Date();
  };

  const handlePayNow = (invoice) => {
    alert("Payment integration coming soon!\n\nInvoice: " + invoice.invoiceNumber + "\nAmount: " + formatCurrency(invoice.total));
  };

  // EXACT ADMIN PORTAL PDF GENERATION
  const generatePDF = (invoice) => {
    try {
      const doc = new jsPDF();

      // Add logo if loaded
      if (logoImage) {
        try {
          doc.addImage(logoImage, "PNG", 20, 15, 20, 20);
          console.log("Logo added to PDF");
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      } else {
        console.log("Logo not loaded, continuing without it");
      }

      // Company details (top right)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Humbertrack", 200, 20, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.text("34 Plantation Drive", 200, 25, { align: "right" });
      doc.text("North Ferriby, HU143BE", 200, 30, { align: "right" });
      doc.text("Tel: 07897 201067", 200, 35, { align: "right" });
      doc.text("Email: installs@humbertrack.co.uk", 200, 40, { align: "right" });

      // Invoice title
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", 20, 50);

      // Invoice details
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const invoiceDate = invoice.createdAt?.toDate
        ? invoice.createdAt.toDate().toLocaleDateString()
        : new Date().toLocaleDateString();
      doc.text(`Invoice No: ${invoice.invoiceNumber || "-"}`, 20, 60);
      doc.text(`Invoice Date: ${invoiceDate}`, 20, 65);

      // Bill to
      doc.setFont("helvetica", "bold");
      doc.text("Bill To:", 20, 75);
      doc.setFont("helvetica", "normal");
      doc.text(invoice.clientName || invoice.customerName || customer.name, 20, 80);
      
      // Split address into multiple lines if needed
      const address = invoice.clientAddress || customer.address || "";
      const addressLines = doc.splitTextToSize(address, 170);
      doc.text(addressLines, 20, 85);
      
      const emailY = 85 + (addressLines.length * 5);
      if (invoice.clientEmail || customer.contactEmail) {
        doc.text(`Email: ${invoice.clientEmail || customer.contactEmail}`, 20, emailY);
      }

      // Table
      const tableStartY = emailY + 10;
      const tableData = invoice.items && invoice.items.length > 0 
        ? invoice.items.map((item) => [
            item.description || "Service",
            item.quantity || 1,
            `£${(item.rate || item.price || 0).toFixed(2)}`,
            `£${((item.quantity || 1) * (item.rate || item.price || 0)).toFixed(2)}`,
          ])
        : [[
            invoice.description || "Service",
            1,
            `£${invoice.total.toFixed(2)}`,
            `£${invoice.total.toFixed(2)}`,
          ]];

      autoTable(doc, {
        startY: tableStartY,
        head: [["Description", "Qty", "Unit", "Total"]],
        body: tableData,
        theme: "plain",
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        styles: {
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: 20, halign: "center" },
          2: { cellWidth: 30, halign: "right" },
          3: { cellWidth: 30, halign: "right" },
        },
      });

      // Totals
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFont("helvetica", "normal");
      doc.text("Subtotal:", 150, finalY, { align: "right" });
      doc.text(`£${invoice.subtotal.toFixed(2)}`, 190, finalY, { align: "right" });

      doc.text(`VAT (${invoice.vatRate || 20}%):`, 150, finalY + 5, { align: "right" });
      doc.text(`£${invoice.vat.toFixed(2)}`, 190, finalY + 5, { align: "right" });

      doc.setFont("helvetica", "bold");
      doc.text("Total:", 150, finalY + 10, { align: "right" });
      doc.text(`£${invoice.total.toFixed(2)}`, 190, finalY + 10, { align: "right" });

      // Payment details
      doc.setFont("helvetica", "bold");
      doc.text("Payment Details:", 20, finalY + 25);
      doc.setFont("helvetica", "normal");
      doc.text("HumberTrack", 20, finalY + 30);
      doc.text("Bank: STARLING BANK", 20, finalY + 35);
      doc.text("Sort Code: 60-83-71", 20, finalY + 40);
      doc.text("Account Number: 3929 5852", 20, finalY + 45);

      // Notes
      if (invoice.notes) {
        doc.setFont("helvetica", "bold");
        doc.text("Notes:", 20, finalY + 55);
        doc.setFont("helvetica", "normal");
        const notesLines = doc.splitTextToSize(invoice.notes, 170);
        doc.text(notesLines, 20, finalY + 60);
      }

      // Footer
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text("Thank you for your business!", 105, 280, { align: "center" });

      // Save
      doc.save(`Invoice-${invoice.invoiceNumber || "draft"}.pdf`);
      console.log("PDF generated successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF: " + error.message);
    }
  };

  // Calculate stats
  const totalInvoices = invoices.length;
  const unpaidInvoices = invoices.filter(inv => inv.status?.toLowerCase() !== "paid").length;
  const paidInvoices = invoices.filter(inv => inv.status?.toLowerCase() === "paid").length;
  const totalOutstanding = invoices
    .filter(inv => inv.status?.toLowerCase() !== "paid")
    .reduce((sum, inv) => sum + (inv.total || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading invoices: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Invoices
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and pay your invoices
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Showing invoices for: {customer.name}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Total Invoices
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {totalInvoices}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Unpaid
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {unpaidInvoices}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Paid
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {paidInvoices}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              Outstanding
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {formatCurrency(totalOutstanding)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Receipt sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No invoices found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your invoices will appear here once jobs are completed
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {invoices.map((invoice) => {
            const overdue = isOverdue(invoice.dueDate, invoice.status);
            
            return (
              <Card
                key={invoice.id}
                sx={{
                  border: overdue ? "2px solid" : "1px solid",
                  borderColor: overdue ? "error.main" : "divider",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    {/* Invoice Icon & Number */}
                    <Grid item xs={12} sm={3}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Receipt sx={{ color: "white" }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Invoice
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {invoice.invoiceNumber || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Details */}
                    <Grid item xs={12} sm={5}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {invoice.jobTemplateName || "Service"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {invoice.description || "No description"}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDate(invoice.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Due: {formatDate(invoice.dueDate)}
                        </Typography>
                      </Stack>
                    </Grid>

                    {/* Amount & Status */}
                    <Grid item xs={12} sm={2}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Amount
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        {formatCurrency(invoice.total)}
                      </Typography>
                      {invoice.vat > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          VAT: {formatCurrency(invoice.vat)}
                        </Typography>
                      )}
                    </Grid>

                    {/* Actions */}
                    <Grid item xs={12} sm={2}>
                      <Stack spacing={1} alignItems="flex-end">
                        <Chip
                          icon={getStatusIcon(invoice.status)}
                          label={overdue ? "OVERDUE" : (invoice.status || "Unpaid")}
                          color={getStatusColor(invoice.status)}
                          size="small"
                        />
                        
                        {invoice.status?.toLowerCase() !== "paid" && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Payment />}
                            onClick={() => handlePayNow(invoice)}
                            sx={{
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            }}
                          >
                            Pay Now
                          </Button>
                        )}
                        
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          onClick={() => generatePDF(invoice)}
                        >
                          Download
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default CustomerInvoices;